import { IDisposable } from "Global/Base/IDisposable";
import { EventsController } from "Global/Dom/EventsController";
import { Event } from "../../../../Global/Base/Events";
import { invariant } from "../../../../Global/Base/Utils/AssertUtils";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { GenericControl as BaseGenericControl } from "../../../UI.BaseControls/GenericControl";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { ILoader } from "../ILoader";
import {
    ControlParams,
    GenericControlParams,
    ICompositeComponent,
    IControl,
    IGenericControl,
    IHTMLElementWithControl,
} from "./ControlInterfaces";
import { IItemRightsCollection } from "../../../../Global/Base/Rights/RightTypes";
import { ControlUtils } from "./ControlUtils";

/**
 * Implementuje úplně základní control - viz {@link IGenericControl}.
 * @includeToDoc
 */
export class GenericControl implements IGenericControl, ICompositeComponent {
    //#region Fields
    private _element: HTMLElement;
    /**
     * Viz {@link IWithOriginalRightsDefinition.originalRightsDefinition}.
     */
    private readonly _originalRightsDefinition: IItemRightsCollection;

    protected _events = new EventsController();
    protected _disposableObjects = new Set<IDisposable>();

    /**
     * Viz {@link ICompositeComponent#isCompositeComponent}
     */
    public isCompositeComponent?: boolean;
    protected _isDisposed: boolean = false;

    //#endregion Fields

    public get element(): HTMLElement {
        return this._element;
    }

    public set element(v: HTMLElement) {
        this._element = v;
    }

    public get originalRightsDefinition(): IItemRightsCollection {
        return this._originalRightsDefinition;
    }

    /** objekty, které se při dispose také uvolní */
    public get disposableObjects(): Set<IDisposable> {
        return this._disposableObjects;
    }

    //#region Constructors
    constructor({ element, originalRightsDefinition }: GenericControlParams) {
        invariant(!!originalRightsDefinition, "[GenericControl] originalRightsDefinition must be present");
        this.element = element;
        this._originalRightsDefinition = originalRightsDefinition;
        (this.element as IHTMLElementWithControl).tscControl = this;
    }
    //#endregion Constructors

    //#region dispose
    public dispose(): void {
        if (this._isDisposed) {
            return;
        }
        this._isDisposed = true;
        this._events?.dispose();
        this._disposableObjects?.forEach(obj => obj.dispose());
        this._disposableObjects.clear();
        if (this.element) {
            this.element.textContent = "";
            (this.element as IHTMLElementWithControl).tscControl = null;
            if (this.element.parentNode)
                this.element.parentNode.removeChild(this.element);
        }
    }

    //#endregion dispose
}

/**
 * Základní needitační control komponenty
 * @includeToDoc
 */
export class Control extends GenericControl implements ILoader, IControl {
    //#region Fields
    public visibilityChanged: Event<Control, boolean> = new Event<Control, boolean>();
    /**
     * Událost vyvolaná při změně viditelnosti prvku ve stránce
     */
    public canValueLoadChanged: Event<Control, boolean> = new Event<Control, boolean>();

    protected _captionLabel: HTMLElement;
    protected _loaderElement: HTMLElement;
    protected _encryptedElement: HTMLElement;
    protected _visible: boolean = true;
    protected _enabled: boolean = false;
    protected _isVisuallyEncrypted: boolean = false;

    private _showLoaderTimer: any;
    private _componentName: string = "";
    private _locKey: string;
    private _lastCanValueLoad: boolean = false;

    /**
     * Oznacuje jestli ma komponenta, obalovaci container. Pokud ano je potreba mu taky nastavit visibilitu
     */
    protected readonly _hasContainerParent: boolean = false;

    //#endregion Fields

    //#region Properties

    public set locKey(value: string) {
        this._locKey = value;
    }

    public get locKey(): string {
        return this._locKey;
    }

    public get componentName(): string {
        return this._componentName;
    }

    public set enabled(value: boolean) {
        this._enabled = value;
    }

    public get enabled(): boolean {
        return this._enabled;
    }

    public set visible(value: boolean) {
        if (this.element == null)
            return;

        this._visible = value;
        const display = value ? "" : "none";
        if (this._hasContainerParent && this.element.parentElement)
            this.element.parentElement.style.display = display;
        this.element.style.display = display;
        this.visibilityChanged.trigger(this, value);
    }

    public get visible(): boolean {
        return this._visible;
    }

    public get isVisuallyEncrypted(): boolean {
        return this._isVisuallyEncrypted;
    }

    public set isVisuallyEncrypted(val: boolean) {
        if (this._isVisuallyEncrypted === val)
            return;

        this._isVisuallyEncrypted = val;
        this.element.classList.toggle("encrypted", val);
        if (this._encryptedElement == null) {
            this._encryptedElement = createElement({ classes: ["encrypted-symbol", "icon", "user-lock"] });
            this.element.insertAdjacentElement("afterbegin", this._encryptedElement);
        }
    }

    /**
     * Vraci informace o tom, zda je mozne nacitat hodnotu (zda je objekt viditelny z pohledu práv)
     */
    public get canValueLoad(): boolean {
        if (this.visible) {
            const ret = BaseGenericControl.allParents(this.element, (el: HTMLElement) => el.style.display !== "none" || el.classList.contains("AlignPanelSubcomponent") || el.classList.contains("AlignDockHelperContainer")); // obalovaci elementy se preskoci
            return ret;
        } else
            return false;
    }

    public showLoader(delay: number = 300): void {
        if (this._showLoaderTimer)
            return;

        this._showLoaderTimer = self.setTimeout(() => {
            this._showLoader();
            this._showLoaderTimer = undefined;
        }, delay);
    }

    public hideLoader(): void {
        if (this._showLoaderTimer)
            clearTimeout(this._showLoaderTimer);
        this._showLoaderTimer = undefined;
        this._hideLoader();
    }

    protected _showLoader(): void {
        if (!this._loaderElement && this.element) {
            this.element.appendChild(this._createLoader());
        }

        if (this._loaderElement)
            this._loaderElement.style.display = "";
        this.element.classList.add("waiting");
    }

    protected _hideLoader(): void {
        if (this._loaderElement)
            this._loaderElement.style.display = "none";
        this.element.classList.remove("waiting");
    }

    public isLoading(): boolean {
        return this._loaderElement != null && this._loaderElement.style.display !== "none";
    }

    protected _createLoader(): HTMLElement {
        this._loaderElement = createElement({ classes: "ElementLoader", css: { display: "none" } });
        const loader = createElement({ classes: "loader" });
        const right = createElement({ classes: "right" });
        const left = createElement({ classes: "left" });

        this._loaderElement.appendChild(loader);

        loader.appendChild(right);
        right.appendChild(createElement({ classes: "border" }));

        loader.appendChild(left);
        left.appendChild(createElement({ classes: "border" }));
        return this._loaderElement;
    }

    /**
     * Vraci rodicovsky element
     */
    protected get parentElement(): HTMLElement {
        return this.element.parentElement;
    }
    //#endregion Properties

    //#region Constructors
    constructor(params: ControlParams) {
        super(params);
        const { element } = params;
        this.locKey = element.getAttribute("data-captionlockey");

        if (this.locKey != null && this.locKey !== "XG-666") {
            Localization.getTextPromise(this.locKey)
                .then(componentName => this._componentName = componentName);

            this._events.add(Localization.languageChanged, async () => {
                this._componentName = await Localization.getTextPromise(this.locKey);
            });
        } else {
            this._componentName = this.getDataAttr("attrname");
        }

        this.element.classList.add("GenericControl");

        if (this.element.parentElement && (this.element.parentElement.classList.contains("ControlContainer") || this.element.parentElement.classList.contains("AlignDockHelperContainer")))
            this._hasContainerParent = true;

        this._events.add(this.visibilityChanged, (_, prm) => {
            if (!this.tryTriggerCanValueLoadChange(this, this.canValueLoad))
                return;

            // je potreba zneviditelnit i container, pokud jsou pres sebe umisteny komponenty a prepina se jejich viditelnost, nelze kliknout na ty, ktere predchazi
            if (this._hasContainerParent && this.element.parentElement)
                this.element.parentElement.style.display = prm ? "" : "none";

            // vyvolani události pro podrizene prvky
            const elementList = Array.from(this.element.children);
            const value = this.canValueLoad;
            for (const elem of elementList) {
                const htcontrol = (elem as IHTMLElementWithControl).tscControl as Control;
                if (htcontrol && htcontrol.canValueLoadChanged) {
                    htcontrol.tryTriggerCanValueLoadChange(htcontrol, value);
                }
            }
        });

        this._setDefaultTooltip();

        if (this.element.previousElementSibling != null && this.element.previousElementSibling.classList.contains("CaptionContainer") && this.element.previousElementSibling.childElementCount > 0) {
            this._captionLabel = this.element.previousElementSibling.firstElementChild as HTMLLabelElement;
        } else if (this.element?.parentElement?.previousElementSibling != null && this.element.parentElement.previousElementSibling.classList.contains("caption-component")) {
            this._captionLabel = this.element.parentElement.previousElementSibling as HTMLLabelElement;
        }
    }
    //#endregion Constructors

    //#region Methods
    /** Nacita hodnotu data- atributu z html, vraci ji jako orezany string. Pokud atribut neexistuje, vraci null.
     * @param attr - nazev atributu bez data- prefixu */
    public getDataAttr(attr: string): string | null {
        return ControlUtils.getDataAttr(attr, this.element);
    }

    /** Nacita hodnotu data- atribut jako boolean; vraci true jedine kdyz hodnota je (case insensitive) retezec 'true', jinak false */
    public getBoolDataAttr(attr: string, defaultValue?: boolean): boolean {
        return ControlUtils.getBoolDataAttr(attr, this.element, defaultValue);
    }

    /** Vraci hodnotu data- atributu jako cislo; pokud atribut neexistuje, je prazdny nebo NaN, vrati null */
    public getNumDataAttr(attr: string): number | null {
        return ControlUtils.getNumDataAttr(attr, this.element);
    }

    /** Vrací objekt, jehož properties jsou všechny data-attributy elementu */
    protected _getDataAttributes(): any {
        const dataAttributes: any = {};
        Object.assign(dataAttributes, this.element.dataset);
        return dataAttributes;
    }

    //#endregion Methods

    protected _setDefaultTooltip(): void {
        if (this.locKey != null)
            Localization.addLocDescData(this.element, this.locKey, "title");
    }

    /**
    * Nastavuje vstupnímu elementu ID, která jej prováže s Caption
    * @param element kterému má být přiřazeno ID
    */
    // Je to řešené tímto způsobem, jelikož je Caption vytvořen dříve než obsah samotných komponent, se kterými má být provázán
    protected _setCaptionReference(element: HTMLElement): void {
        if (this._captionLabel) { // Neexistuje-li caption, není třeba nastavovat ID
            element.id = this._captionLabel.getAttribute("for");
        }
    }

    /**
     * Metoda vyhodnotí zda se má vyvolat událost canValueLoadChange, případně událost vyvolá. Vrací true, pokud byla událost vyvolána.
     */
    public tryTriggerCanValueLoadChange(_sender: Control, value: boolean): boolean {
        if (value === this._lastCanValueLoad)
            return false;

        this._lastCanValueLoad = value;
        this.canValueLoadChanged.trigger(this, value);
        return true;
    }

    public override dispose(): void {
        if (this._isDisposed) {
            return;
        }

        if (this._showLoaderTimer) {
            self.clearTimeout(this._showLoaderTimer);
        }
        this.visible = false;
        super.dispose();
    }
}
